import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, ShieldCheck, ShieldQuestion, Eye } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import LoadFailureNote from "../components/LoadFailureNote";
import { fetchAdminGuideList, publishGuide, unpublishGuide } from "../lib/guides";
import { STATE_FILTERS, categoryOptions, filterGuides } from "../lib/guideConsole";

const NAVY = "#0B2E59";

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-NZ");
}

/**
 * The guide review console (issue #219) — every processed guide, its
 * state, and Publish/Unpublish. This is the "document management system"
 * Andreas asked for: it lists the FULL corpus the Worker bundles
 * (`GET /admin/guides`, driven from `ALL_GUIDES`, not from what's already
 * published), so a guide sitting unreviewed is visible here even though
 * nothing in the player-facing app can ever show it.
 *
 * Publishing/unpublishing here is the exact same write the guide page's
 * "Mark as reviewed" button makes (`POST`/`DELETE .../review`) — this
 * screen is the bulk-visible companion to that per-guide action, per
 * issue #219's "bulk-visible status, since there are 101 guides, not
 * twelve" (44, currently — the rest await #105/the wider corpus import).
 */
export default function AdminGuides() {
  const [guides, setGuides] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [category, setCategory] = useState("all");
  const [state, setState] = useState("all");
  const [actingOn, setActingOn] = useState(null); // `${category}/${slug}` mid-request

  useEffect(() => {
    let cancelled = false;
    fetchAdminGuideList()
      .then((rows) => {
        if (cancelled) return;
        setGuides(rows);
        setLoadFailed(false);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) { setLoadFailed(true); setLoaded(true); }
      });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const setRowState = (g, patch) =>
    setGuides((rows) => rows.map((r) => (r.category === g.category && r.slug === g.slug ? { ...r, ...patch } : r)));

  const doPublish = async (g) => {
    const key = `${g.category}/${g.slug}`;
    setActingOn(key);
    try {
      const review = await publishGuide(g.category, g.slug);
      setRowState(g, { state: "published", reviewer_name: review.reviewerName, reviewed_at: review.reviewedAt });
      toast.success(`${g.title} is now published`);
    } catch {
      toast.error(`Could not publish ${g.title}. Please try again.`);
    }
    setActingOn(null);
  };

  const doUnpublish = async (g) => {
    const key = `${g.category}/${g.slug}`;
    setActingOn(key);
    try {
      await unpublishGuide(g.category, g.slug);
      setRowState(g, { state: "unreviewed", reviewer_name: null, reviewed_at: null });
      toast.success(`${g.title} is unpublished`);
    } catch {
      toast.error(`Could not unpublish ${g.title}. Please try again.`);
    }
    setActingOn(null);
  };

  const visible = filterGuides(guides, { category, state });
  const unreviewedCount = guides.filter((g) => g.state === "unreviewed").length;

  return (
    <div className="space-y-6" data-testid="admin-guides-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Admin · Guides</p>
        <h1 className="font-display font-black text-2xl text-slate-900 mt-1">Guide review console</h1>
        <p className="text-slate-500 text-sm mt-1">
          {loaded && !loadFailed
            ? `${guides.length} guides imported · ${unreviewedCount} awaiting review`
            : "Every guide the app has imported, and its publish state."}
        </p>
      </div>

      {loadFailed ? (
        <Card className="border-slate-200">
          <CardContent className="p-10 flex">
            <LoadFailureNote what="the guide list" onRetry={() => setReloadKey((k) => k + 1)} testId="admin-guides-failed" />
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="min-w-0 flex-1">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="admin-guides-filter-category" className="h-11 sm:h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categoryOptions(guides).map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-0 flex-1">
              <Select value={state} onValueChange={setState}>
                <SelectTrigger data-testid="admin-guides-filter-state" className="h-11 sm:h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATE_FILTERS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {loaded && visible.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-12 text-center" data-testid="admin-guides-empty">
                <p className="text-slate-500">No guides match this filter.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2" data-testid="admin-guides-list">
              {visible.map((g) => {
                const key = `${g.category}/${g.slug}`;
                const acting = actingOn === key;
                const published = g.state === "published";
                return (
                  <Card key={key} className="border-slate-200" data-testid={`admin-guide-${g.category}-${g.slug}`}>
                    <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900 break-words">{g.title}</span>
                          <Badge variant="outline" className="uppercase text-[10px]">{g.category.replace(/-/g, " ")}</Badge>
                          {published ? (
                            <Badge className="bg-emerald-600 hover:bg-emerald-600 gap-1">
                              <ShieldCheck size={11} /> Published
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="gap-1 text-amber-700 border-amber-300">
                              <ShieldQuestion size={11} /> Unreviewed
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {published
                            ? `Reviewed by ${g.reviewer_name} · ${fmtWhen(g.reviewed_at)}`
                            : "No reviewer on record yet."}
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Link
                          to={`/learn/${g.category}/${g.slug}${published ? "" : "?review=1"}`}
                          data-testid={`admin-guide-view-${g.category}-${g.slug}`}
                        >
                          <Button variant="outline" size="sm" className="w-full sm:w-auto h-11 sm:h-9">
                            <Eye size={12} className="mr-1.5" /> View
                          </Button>
                        </Link>
                        {published ? (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={acting}
                            onClick={() => doUnpublish(g)}
                            className="w-full sm:w-auto h-11 sm:h-9 text-red-700 border-red-200 hover:bg-red-50"
                            data-testid={`admin-guide-unpublish-${g.category}-${g.slug}`}
                          >
                            {acting ? "Working…" : "Unpublish"}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            disabled={acting}
                            onClick={() => doPublish(g)}
                            className="w-full sm:w-auto h-11 sm:h-9"
                            style={{ background: NAVY }}
                            data-testid={`admin-guide-publish-${g.category}-${g.slug}`}
                          >
                            {acting ? "Working…" : "Publish"}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {!loadFailed && guides.length === 0 && loaded && (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center flex flex-col items-center gap-2" data-testid="admin-guides-none-imported">
            <BookOpen size={24} className="text-slate-300" />
            <p className="text-slate-500">No guides have been imported yet.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
